<script lang="ts">
	/**
	 * The single block a list, grid or table body renders when it has nothing to
	 * show — because it is loading, because it is genuinely empty, or because it
	 * failed.
	 *
	 * All three are the same shape (centred, generously padded, muted) so the
	 * area does not jump as a list moves between them; only the icon and the
	 * colour change. Having one component for all three is what stops each page
	 * from inventing its own spinner-in-a-div.
	 *
	 * Inside a Flowbite table, wrap it: `<TableBodyRow><TableBodyCell
	 * colspan={n}><EmptyState … /></TableBodyCell></TableBodyRow>`.
	 *
	 * Slot: default — actions ("Create the first one", "Retry").
	 */
	import { Spinner } from 'flowbite-svelte';

	export let variant: 'empty' | 'loading' | 'error' = 'empty';

	/** Headline. Falls back to a sensible default per variant. */
	export let title: string = '';
	/** Optional second line explaining what to do about it. */
	export let description: string = '';

	/** Font Awesome solid icon name, without the `fa-` prefix. Ignored while loading. */
	export let faIcon: string = '';

	/** Tighter padding and no icon — for narrow columns and small panels. */
	export let compact: boolean = false;

	let className: string = '';
	export { className as class };

	const defaultTitles: Record<string, string> = {
		empty: 'Nothing here yet.',
		loading: 'Loading…',
		error: 'Something went wrong.'
	};

	const defaultIcons: Record<string, string> = {
		empty: 'inbox',
		loading: '',
		error: 'triangle-exclamation'
	};

	$: resolvedTitle = title || defaultTitles[variant];
	$: resolvedIcon = faIcon || defaultIcons[variant];
	// Errors are the one case worth colouring — everything else stays muted so
	// an empty list never looks like a warning.
	$: toneClass = variant === 'error' ? 'text-red-500 dark:text-red-400' : 'text-default';
</script>

<div class="{className} w-full flex flex-col justify-center items-center {compact ? 'p-2 gap-1' : 'p-10 gap-3'}">
	{#if variant === 'loading'}
		<Spinner size={compact ? '5' : '8'} />
	{:else if resolvedIcon && !compact}
		<i class="fa-solid fa-{resolvedIcon} fa-2x {toneClass} opacity-60" />
	{/if}

	<div class="text-center {toneClass} {compact ? 'text-xs font-medium uppercase' : ''}">
		{resolvedTitle}
	</div>

	{#if description && !compact}
		<div class="text-center text-sm text-default max-w-md">{description}</div>
	{/if}

	{#if $$slots.default}
		<div class="flex flex-wrap items-center justify-center gap-2 {compact ? '' : 'mt-2'}">
			<slot />
		</div>
	{/if}
</div>
